import { isEmpty, isNil } from 'lodash';
import { type IntlFormatters } from 'react-intl';

import { isSyncWithExactOnline } from '../integrations';
import { isDiscount } from '../leaseutils';
import { roundAtSecondDecimal } from '../numbers';
import { isNilOrEmpty } from '../objectUtils';
import { Lease, LeaseVariousOperation, LeaseVariousOperationDTO, VatRate, VatRateDTO } from '../types';

import { ACCOUNT_TURNOVER_FURNISHED_RENT_CLASS, ACCOUNT_TURNOVER_RENT_CLASS } from './accountClasses';
import { DEFAULT_VAT_RATES } from './accountutils';

const getVatRateTranslatedLabel = (vatRate: VatRate, formatMessage: IntlFormatters['formatMessage']) => {
  const avoidTranslate = vatRate.label.includes('%') || isSyncWithExactOnline(vatRate.customValues);
  return avoidTranslate ? vatRate.label : formatMessage({ id: vatRate.label, defaultMessage: vatRate.label });
};

export type VatRateTranslated = Pick<VatRate, 'id' | 'label' | 'value'>;

export const getTranslatedVatRates = (
  vatRates: VatRate[] | null,
  formatMessage: IntlFormatters['formatMessage'],
  country = 'BE',
  excludeIntracommunity = false,
  ownerId: string | null | undefined = undefined
): VatRateTranslated[] => {
  const vatRatesOrDefault = !isNilOrEmpty(vatRates)
    ? vatRates.filter((vatRate) => !vatRate.ownerId)
    : DEFAULT_VAT_RATES;
  const filteredVatRatesForOwner = (vatRates ?? []).filter((vatRate) => ownerId === vatRate.ownerId);
  const filteredVatRates = !isEmpty(filteredVatRatesForOwner) ? filteredVatRatesForOwner : vatRatesOrDefault;
  const translatedVatRates = filteredVatRates.reduce((acc: VatRateTranslated[], vatRate) => {
    // Only propose vat rates from country and ignore intracommunity
    if (vatRate.country === country && !(excludeIntracommunity && vatRate.label.includes('intracommunity'))) {
      const label = getVatRateTranslatedLabel(vatRate as VatRate, formatMessage);
      acc.push({ label, value: vatRate.value, id: (vatRate as VatRate)?.id });
    }
    return acc;
  }, []);
  return translatedVatRates.sort((translatedVat1, translatedVat2) => {
    if (isNil(translatedVat1.value)) return -1;
    if (isNil(translatedVat2.value)) return 1;
    return translatedVat1.value < translatedVat2.value ? -1 : 1;
  });
};

export const getExcludedVatAmountFromIncludedVatAmount = (
  amountVATIncluded: number,
  vatRate: number | undefined | null
) => {
  if (!vatRate) return amountVATIncluded;
  const ratio = 1 + vatRate / 100;
  return roundAtSecondDecimal(amountVATIncluded / ratio);
};

export const getIncludedVatAmountFromExcludedVatAmount = (amountVATExcluded: number, vatRate?: number | null) => {
  if (!vatRate) return amountVATExcluded;
  const ratio = 1 + vatRate / 100;
  return roundAtSecondDecimal(amountVATExcluded * ratio);
};

const PERCENT_DIVISOR = 100;

export const getPostingVatAmount = (amountVatExcluded: number, vatRateValue?: number | null): number =>
  isNil(vatRateValue) || vatRateValue === 0
    ? 0
    : roundAtSecondDecimal((vatRateValue * roundAtSecondDecimal(amountVatExcluded)) / PERCENT_DIVISOR);

export const getPostingTotalAmount = (amountVatExcluded: number, vatRateValue?: number | null): number => {
  const roundedAmountVatExcluded = roundAtSecondDecimal(amountVatExcluded);
  return roundAtSecondDecimal(roundedAmountVatExcluded + getPostingVatAmount(roundedAmountVatExcluded, vatRateValue));
};

export const getLeaseVariousOperationVatRate = (
  leaseVariousOperation: LeaseVariousOperation | LeaseVariousOperationDTO,
  leaseVatRateRent: VatRate | VatRateDTO | null | undefined
) => (isDiscount(leaseVariousOperation) ? leaseVatRateRent : leaseVariousOperation.vatRate);

export const applyVatToLeaseVariousOperationAmount = (
  leaseVariousOperation: LeaseVariousOperation | LeaseVariousOperationDTO,
  leaseVatRateRent: VatRate | VatRateDTO | null | undefined
): LeaseVariousOperation | LeaseVariousOperationDTO => ({
  ...leaseVariousOperation,
  amount: getIncludedVatAmountFromExcludedVatAmount(
    leaseVariousOperation.amount,
    getLeaseVariousOperationVatRate(leaseVariousOperation, leaseVatRateRent)?.value
  ),
});

/**
 * Splits a new monthly provision, VAT included, across provision lines in proportion to their current amounts VAT
 * included, and returns the new amount of each line without VAT, by line id. Lines all at zero share it evenly.
 */
export const splitMonthlyProvisionVatIncluded = (
  provisionLvos: (LeaseVariousOperation | LeaseVariousOperationDTO)[],
  newMonthlyProvisionVatIncluded: number
): Record<string, number> => {
  const getAmountVatIncluded = (lvo: LeaseVariousOperation | LeaseVariousOperationDTO) =>
    getIncludedVatAmountFromExcludedVatAmount(lvo.amount, lvo.vatRate?.value);
  const currentMonthlyProvisionVatIncluded = roundAtSecondDecimal(
    provisionLvos.reduce((total, lvo) => total + getAmountVatIncluded(lvo), 0)
  );
  return Object.fromEntries(
    provisionLvos.map((lvo) => [
      lvo.id,
      roundAtSecondDecimal(
        getExcludedVatAmountFromIncludedVatAmount(
          currentMonthlyProvisionVatIncluded === 0
            ? newMonthlyProvisionVatIncluded / provisionLvos.length
            : (getAmountVatIncluded(lvo) / currentMonthlyProvisionVatIncluded) * newMonthlyProvisionVatIncluded,
          lvo.vatRate?.value
        )
      ),
    ])
  );
};

export const getVatRateIdToApply = (lease: Lease, selectedAccountLabelClass: number) => {
  if (
    !isNilOrEmpty(lease.vatRateRentId) &&
    [ACCOUNT_TURNOVER_RENT_CLASS, ACCOUNT_TURNOVER_FURNISHED_RENT_CLASS].includes(selectedAccountLabelClass)
  ) {
    return lease.vatRateRentId;
  }
  return undefined;
};

export const getBusinessNumberByVatNumber = (vatNumber: string | undefined | null) => vatNumber?.split('BE')[1];
